/**
 * The hosted page profile's rules on the portal side (contractor pages phase
 * 1): limits that mirror the API's strict schema, phone normalisation to
 * E.164, the completeness checklist's words, and the defaults a profile
 * fills in when the API leaves a key out.
 *
 * The limits are the server's (`services/hosted-page-profile.ts`, migration
 * 100) — keep them in step. The server still validates everything; these make
 * the form say so before a round trip.
 */
import type {
  HostedPageCompletenessKey,
  HostedPageCta,
  HostedPageHours,
  HostedPageProfileInput,
} from '../api/types';
import { HOSTED_PAGE_WEEKDAYS } from '../api/types';

export const LIMITS = {
  serviceAreaSummary: 200,
  postalCodes: 100,
  services: 20,
  serviceName: 80,
  serviceDescription: 240,
  hoursNote: 160,
  licenseNumber: 40,
  licenseAuthority: 80,
  insuranceNote: 160,
  about: 2000,
  testimonials: 10,
  testimonialAuthor: 60,
  testimonialText: 400,
  faq: 10,
  faqQuestion: 160,
  faqAnswer: 600,
  ctaLabel: 40,
  quoteIntro: 240,
  gallery: 12,
  foundedMin: 1900,
  foundedMax: 2100,
} as const;

/** The server's E.164 rule (8–15 digits). */
export const E164_RE = /^\+[1-9][0-9]{7,14}$/;
/** A US ZIP code or a prefix of one (3–5 digits; the API refuses shorter). */
export const ZIP_PREFIX_RE = /^[0-9]{3,5}$/;
export const EMAIL_RE = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;
/** 24-hour `HH:MM`; a closing time may be `24:00` (midnight). */
export const HHMM_RE = /^(([01][0-9]|2[0-3]):[0-5][0-9]|24:00)$/;

/**
 * "(206) 555-0100" → "+12065550100". US/Canada numbers without a country
 * code get +1; anything starting with + keeps its own. Input that is not a
 * phone number comes back trimmed and unchanged, so validation can say so.
 */
export function toE164(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/[^0-9+\s().\-/]/.test(trimmed.replace(/^tel:/i, ''))) return trimmed;
  const plus = trimmed.replace(/^tel:/i, '').trim().startsWith('+');
  const digits = trimmed.replace(/[^0-9]/g, '');
  if (plus) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.startsWith('00') && digits.length > 9) return `+${digits.slice(2)}`;
  return trimmed;
}

/** +12065550100 → (206) 555-0100; anything else as stored. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '';
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/** The checklist's words, in the order the page shows them. */
export const COMPLETENESS_LABELS: Record<HostedPageCompletenessKey, string> = {
  phone: 'Add a phone number',
  photos: 'Add at least 3 photos',
  services: 'List 2 or more services',
  about: 'Write about your business',
  hours: 'Add your hours',
  license: 'Add a license or insurance',
  testimonial: 'Add a customer testimonial',
  testimonials: 'Add a customer testimonial',
};

/** Which card on the editor fixes each checklist item (its element id). */
export const COMPLETENESS_TARGET: Record<HostedPageCompletenessKey, string> = {
  phone: 'hp-contact',
  photos: 'hp-photos',
  services: 'hp-services',
  about: 'hp-about',
  hours: 'hp-hours',
  license: 'hp-about',
  testimonial: 'hp-testimonials',
  testimonials: 'hp-testimonials',
};

export const WEEKDAY_LABELS: Record<(typeof HOSTED_PAGE_WEEKDAYS)[number], string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export const EMPTY_CTA: HostedPageCta = { primary_label: null, quote_label: null, quote_enabled: false, quote_intro: null };

export function emptyHours(): HostedPageHours {
  return { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [], emergency_24_7: false };
}

/**
 * The profile with every key present: a page that has never been edited (or
 * an older API) may leave keys out or send null for a list.
 */
export function normaliseProfile(raw: Partial<HostedPageProfileInput> | null | undefined): HostedPageProfileInput {
  const p = raw ?? {};
  const area = p.service_area && typeof p.service_area === 'object' ? p.service_area : null;
  const list = <T,>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);
  let hours: HostedPageHours | null = null;
  if (p.hours) {
    hours = emptyHours();
    for (const d of HOSTED_PAGE_WEEKDAYS) hours[d] = list(p.hours[d]).map((r) => [r[0], r[1]] as [string, string]);
    hours.emergency_24_7 = Boolean(p.hours.emergency_24_7);
  }
  return {
    phone: p.phone ?? null,
    show_call_button: p.show_call_button ?? true,
    email: p.email ?? null,
    service_area_summary: p.service_area_summary ?? area?.summary ?? null,
    service_area_postal_codes: list(p.service_area_postal_codes ?? area?.postal_codes),
    trades: list(p.trades),
    services: list(p.services).map((s) => ({ name: s.name ?? '', description: s.description ?? null })),
    hours,
    hours_note: p.hours_note ?? null,
    license: p.license ? { number: p.license.number ?? '', state: p.license.state ?? null, authority: p.license.authority ?? null } : null,
    insured: Boolean(p.insured),
    insurance_note: p.insurance_note ?? null,
    founded_year: typeof p.founded_year === 'number' ? p.founded_year : null,
    about: p.about ?? null,
    testimonials: list(p.testimonials).map((t) => ({ author: t.author ?? '', text: t.text ?? '', rating: t.rating ?? null, date: t.date ?? null })),
    faq: list(p.faq).map((f) => ({ q: f.q ?? '', a: f.a ?? '' })),
    cta: { ...EMPTY_CTA, ...(p.cta ?? {}) },
    logo_asset_id: p.logo_asset_id ?? null,
  };
}

export type ServiceAreaWire = 'flat' | 'nested';

/**
 * How this API spells the service area: the agreed contract's two flat keys
 * (`service_area_summary`, `service_area_postal_codes`) or the nested
 * `service_area: { summary, postal_codes }`. Both PUT schemas are strict, so
 * the editor writes back the spelling the GET used.
 */
export function serviceAreaWire(raw: Partial<HostedPageProfileInput> | null | undefined): ServiceAreaWire {
  if (!raw) return 'flat';
  const flat = 'service_area_summary' in raw || 'service_area_postal_codes' in raw;
  return !flat && raw.service_area && typeof raw.service_area === 'object' ? 'nested' : 'flat';
}

/** Trimmed text, or null when empty (the API stores "none" as null). */
export function textOrNull(value: string): string | null {
  const t = value.trim();
  return t ? t : null;
}

export interface QuoteFormBlock {
  /** Plain-English reason the switch cannot be turned on. */
  message: string;
  /** Where to fix it, when the portal has a screen for that. */
  fix?: { to: string; label: string };
  /**
   * Whether it stops the switch being turned on. A page that isn't showing
   * yet can still have the form switched on ahead of time; the notice says
   * it won't appear until the page does.
   */
  blocksSwitch: boolean;
}

/**
 * Why "Request a quote form" cannot be switched on, in words, or null when
 * the only thing keeping it off is the switch itself. The API's `reason` is a
 * short code; an unknown code that reads as a sentence is shown as is.
 */
export function quoteFormBlock(reason: string | null | undefined): QuoteFormBlock | null {
  if (!reason) return null;
  const r = reason.toLowerCase();
  // The switch being off is not a reason to keep the switch disabled.
  if (/(^|_)(cta|quote)_(disabled|off|not_enabled)$|switched_off|turned_off|^disabled$|^not_enabled$/.test(r)) return null;
  if (r.includes('routing') || r.includes('mode')) {
    return {
      message:
        'The quote form sends requests to your own team, but your published “Where leads go” setting sends leads somewhere else. Choose your team (dispatch, booking or a call) and publish, then switch this on.',
      fix: { to: '/setup/routing', label: 'Change where leads go' },
      blocksSwitch: true,
    };
  }
  if (r.includes('served') || r.includes('not_live') || r.includes('page_off') || r.includes('page_disabled')) {
    return {
      message: 'Your page isn’t showing yet, so neither is the quote form. Turn the page on and make sure its live site key is enabled on Sites & keys.',
      fix: { to: '/sites', label: 'Go to Sites & keys' },
      blocksSwitch: false,
    };
  }
  if (r.includes('identity_token')) {
    return {
      message:
        'Your widget settings require a signed identity token, which a visitor to your page doesn’t have. Turn that requirement off to use the quote form.',
      fix: { to: '/config', label: 'Open widget settings' },
      blocksSwitch: true,
    };
  }
  if (r.includes('required_fields')) {
    return {
      message:
        'Your widget settings make a contact field required that the quote form doesn’t ask for (it asks for name, phone, email and ZIP). Make the others optional to use the quote form.',
      fix: { to: '/config', label: 'Open widget settings' },
      blocksSwitch: true,
    };
  }
  if (r.includes('consent')) {
    return {
      message: 'Your widget has no published consent wording yet, which the quote form shows next to its checkbox. Publish your widget settings first.',
      fix: { to: '/config', label: 'Open widget settings' },
      blocksSwitch: true,
    };
  }
  if (r.includes('flag') || r.includes('feature') || r.includes('unavailable')) {
    return { message: 'The quote form isn’t available for your account yet.', blocksSwitch: true };
  }
  return { message: /\s/.test(reason) ? reason : 'The quote form can’t be switched on for this page right now.', blocksSwitch: true };
}
